import { AdminUIActionTypes, AdminErrorMessages, removeNonAsciiChars } from "src/common";
import Papa from "papaparse";

export enum FileUploadTypes {
  CSV = ".csv",
  CERTIFICATE = ".crt, .pem, .cer"
}

// These are the user_id/user email column names for each one of the csv files we provide in Admin.
export enum UserIdColumn {
  DOMAIN_OR_EMAIL = "Domain or User Address",
  USER_ADDRESS = "User Address",
  EMAIL_ADDRESS = "Email Address"
}

// Description: Parses a CSV file and processes its data using the provided handleAction callback.
function parseCSVFile(data: File, handleAction: (params: any) => void) {
  Papa.parse(data, {
    header: true,
    skipEmptyLines: true,
    delimiter: ",",
    transformHeader: (header) => header.trim(),
    transform: (value: string, column: string | number) => {
      let _value;
      if (column === UserIdColumn.DOMAIN_OR_EMAIL || column === UserIdColumn.USER_ADDRESS || column === UserIdColumn.EMAIL_ADDRESS) {
        _value = value.trim().toLowerCase();
      } else {
        _value = value.trim();
      }
      return removeNonAsciiChars(_value);
    },
    complete: function (results: Papa.ParseResult<any>) {
      return handleAction({ actionType: AdminUIActionTypes.CsvData, params: results });
    }
  });
} 

// Description: Reads and extracts a certificate from a file, then processes it using the provided handleAction callback.
function parseCertificateFile(data: File, handleAction: (params: any) => void) {
  const reader = new FileReader();
  reader.onload = () => {
    const certificate = reader.result as string;
    const startIndex = certificate.indexOf("-----BEGIN CERTIFICATE-----");
    const endIndex = certificate.indexOf("-----END CERTIFICATE-----");
    if (startIndex !== -1 && endIndex !== -1) {
      const extractedCertificate = certificate.substring(startIndex, endIndex + "-----END CERTIFICATE-----".length);
      handleAction({ actionType: AdminUIActionTypes.DeviceCertificateData, params: { cert: extractedCertificate } });
    } else {
      handleAction({ actionType: AdminUIActionTypes.PageError, params: { message: AdminErrorMessages.error_invalid_certificate_format } });
    }
  };
  reader.readAsText(data);
}

// Description: Determines the file type and delegates parsing to the appropriate function, then processes the result using handleAction.
export function parseFile(data: File, type: FileUploadTypes, handleAction: (params: any) => void) {
  switch (type) {
    case FileUploadTypes.CSV:
      parseCSVFile(data, handleAction);
      break;
    case FileUploadTypes.CERTIFICATE:
      parseCertificateFile(data, handleAction);
      break;
  }
}

// Description: Returns the appropriate error message for the given file upload type.
export function getError(type: FileUploadTypes) {
  switch (type) {
    case FileUploadTypes.CSV:
      return AdminErrorMessages.error_csv_wrong_file_type;
    case FileUploadTypes.CERTIFICATE:
      return AdminErrorMessages.error_invalid_certificate_extension;
  }
}

// Description: Tests if the file name matches the expected extension(s) for the given file upload type.
export function testExtension(name: string, type: FileUploadTypes) {
  switch (type) {
    case FileUploadTypes.CSV:
      return /^.*\.(csv|CSV)$/.test(name);
    case FileUploadTypes.CERTIFICATE:
      return /^.*\.(crt|pem|cer)$/.test(name);
  }
}

// Description: Returns the drag-and-drop title for the given file upload type.
export function getTitle(type: FileUploadTypes) {
  switch (type) {
    case FileUploadTypes.CSV:
      return "Drag .CSV file here";
    case FileUploadTypes.CERTIFICATE:
      return "Drag .CRT/.PEM/.CER file here";
  }
}
